import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useChildren } from "@/context/ChildrenContext";
import { Sun, Sparkles } from "lucide-react";
import { toast } from "sonner";

const AGE_GROUPS = ["0-2", "3-5", "6-9", "10-12", "13+"];

function getBucket(age) {
  if (age <= 2) return "0-2";
  if (age <= 5) return "3-5";
  if (age <= 9) return "6-9";
  if (age <= 12) return "10-12";
  return "13+";
}

export default function RitualPage() {
  const { activeChild } = useChildren();
  const [ageGroup, setAgeGroup] = useState(activeChild ? getBucket(activeChild.age) : "3-5");
  const [ritual, setRitual] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [bouncing, setBouncing] = useState(null);

  useEffect(() => {
    if (activeChild) setAgeGroup(getBucket(activeChild.age));
  }, [activeChild]);

  const load = async () => {
    const { data } = await api.get(`/ritual/${ageGroup}`);
    setRitual(data);
    setCompleted(data.completed || []);
  };

  useEffect(() => {
    load();
  }, [ageGroup]);

  const toggle = async (i) => {
    setBouncing(i);
    setTimeout(() => setBouncing(null), 400);
    const { data } = await api.post("/ritual/check", { age_group: ageGroup, step_index: i });
    setCompleted(data.completed);
    if (data.completed.includes(i) && ritual) {
      toast.success(`Bravo ! ${ritual.steps[i].emoji} ${ritual.steps[i].title}`);
      if (data.completed.length === ritual.steps.length) {
        toast.success("🌟 Rituel du matin accompli !");
      }
    }
  };

  const progress = ritual ? (completed.length / ritual.steps.length) * 100 : 0;

  return (
    <div className="space-y-6" data-testid="ritual-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl bg-[#FFF4DC] grid place-items-center">
          <Sun className="h-6 w-6 text-[#E8899F]" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Rituel du matin
          </h1>
          <p className="text-[#77849C] text-sm">
            {activeChild ? `Adapté à ${activeChild.name}` : "Choisis une tranche d'âge"}
          </p>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {AGE_GROUPS.map((g) => (
          <button
            key={g}
            data-testid={`ritual-age-${g}`}
            onClick={() => setAgeGroup(g)}
            className={`rounded-full px-5 py-2 text-sm font-semibold whitespace-nowrap transition ${
              ageGroup === g ? "bg-[#23375E] text-white" : "bg-white border border-[#E3E8F1] text-[#44506B] hover:border-[#23375E]"
            }`}
          >
            {g} ans
          </button>
        ))}
      </div>

      <div
        className="rounded-3xl p-6 sm:p-8"
        style={{ background: "linear-gradient(135deg, #FFF4DC 0%, #FBE2E9 60%, #E6F5EF 100%)" }}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#16233D]">Aujourd'hui</p>
            <h2 className="text-2xl font-bold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>
              {completed.length} / {ritual?.steps.length || 0} accomplies
            </h2>
          </div>
          {progress === 100 && <Sparkles className="h-10 w-10 text-[#E8899F]" />}
        </div>
        <div className="mt-4 h-3 bg-white/50 rounded-full overflow-hidden">
          <div
            className="h-full transition-all duration-500"
            style={{ width: `${progress}%`, background: "#23375E" }}
          />
        </div>
      </div>

      <div className="grid gap-4">
        {ritual?.steps.map((s, i) => {
          const done = completed.includes(i);
          return (
            <button
              key={i}
              data-testid={`ritual-step-${i}`}
              onClick={() => toggle(i)}
              className={`soft-card p-5 flex items-center gap-4 text-left transition-all ${
                done ? "!bg-[#D2EDE1] !border-[#23375E]" : "hover:!border-[#23375E]"
              } ${bouncing === i ? "scale-95" : ""}`}
              style={{ transition: "transform 200ms ease" }}
            >
              <div
                className={`h-14 w-14 rounded-2xl grid place-items-center text-3xl transition ${
                  done ? "bg-white" : "bg-[#EDF2F9]"
                }`}
              >
                {s.emoji}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-semibold ${done ? "line-through text-[#16233D]" : "text-[#16233D]"}`}>
                  {s.title}
                </p>
                <p className="text-sm text-[#77849C]">{s.desc}</p>
              </div>
              <div
                className={`h-7 w-7 rounded-full border-2 grid place-items-center transition ${
                  done ? "bg-[#23375E] border-[#23375E]" : "border-[#E3E8F1]"
                }`}
              >
                {done && <span className="text-white text-sm">✓</span>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
